/**
 * Overview state shared between the diorama (R3F) and the Story panel: what
 * is hovered on either side, the traffic source and contact count, packets in
 * flight, per-link counters and the "back to the overview pose" request.
 * Three-free: the Story panel is in the shell chunk.
 */
import { create } from 'zustand';
import { FLOW_STEP_COUNT } from './flowReference';

export type PacketSource = 'live' | 'sim';

export interface LinkStats {
  packetsPerMinute: number;
  /** Wall-clock ms of the last spawn on this link. */
  lastPacketMs: number | null;
  total: number;
}

export const EMPTY_LINK_STATS: LinkStats = { packetsPerMinute: 0, lastPacketMs: null, total: 0 };

export interface OverviewState {
  /** Diorama node under the pointer (scene or label); the panel and the links follow it. */
  hoveredNodeId: string | null;
  /** data-flow stage id hovered in the Story panel; the diorama glows the nodes that carry it. */
  highlightedStageId: number | null;
  /** Illustrative traffic is always simulated; contacts are calculated visibility. */
  source: PacketSource;
  contacts: number;
  selectedStageId: number;
  /** null keeps the whole campus visible; a chosen stage starts its focused scene. */
  focusedStageId: number | null;
  demoPlaying: boolean;
  setDemoPlaying: (playing: boolean) => void;
  selectStage: (stageId: number) => void;
  inFlight: number;
  links: Record<string, LinkStats>;
  /** Bumped by "Reset view"; the camera rig flies back to the overview pose. */
  poseRequest: number;
  /** True when any node fell back to a placeholder model (the HUD says so). */
  placeholderModels: boolean;

  setHovered: (nodeId: string | null) => void;
  setPlaceholderModels: (v: boolean) => void;
  setHighlightedStage: (stageId: number | null) => void;
  setTraffic: (traffic: { source: PacketSource; contacts: number }) => void;
  setInFlight: (count: number) => void;
  /** Merges per-link stats; links not in the patch keep their values. */
  setLinkStats: (patch: Record<string, LinkStats>) => void;
  resetLinkStats: () => void;
  requestOverviewPose: () => void;
}

export const useOverview = create<OverviewState>()((set) => ({
  hoveredNodeId: null,
  highlightedStageId: null,
  source: 'sim',
  contacts: 0,
  selectedStageId: 1,
  focusedStageId: null,
  demoPlaying: false,
  setDemoPlaying: (demoPlaying) => set({ demoPlaying }),
  selectStage: (selectedStageId) =>
    set((s) =>
      !Number.isInteger(selectedStageId) || selectedStageId < 1 || selectedStageId > FLOW_STEP_COUNT
        ? s
        : { selectedStageId, focusedStageId: selectedStageId, poseRequest: s.poseRequest + 1 },
    ),
  inFlight: 0,
  links: {},
  poseRequest: 0,
  placeholderModels: false,

  // Returning the state itself skips the notification: subscribers only wake for real changes.
  setHovered: (hoveredNodeId) => set((s) => (s.hoveredNodeId === hoveredNodeId ? s : { hoveredNodeId })),
  setPlaceholderModels: (placeholderModels) =>
    set((s) => (s.placeholderModels === placeholderModels ? s : { placeholderModels })),
  setHighlightedStage: (highlightedStageId) =>
    set((s) => (s.highlightedStageId === highlightedStageId ? s : { highlightedStageId })),
  setTraffic: ({ source, contacts }) =>
    set((s) => (s.source === source && s.contacts === contacts ? s : { source, contacts })),
  setInFlight: (inFlight) => set((s) => (s.inFlight === inFlight ? s : { inFlight })),
  setLinkStats: (patch) => set((s) => ({ links: { ...s.links, ...patch } })),
  resetLinkStats: () => set({ links: {}, inFlight: 0 }),
  requestOverviewPose: () =>
    set((s) => ({
      focusedStageId: null,
      demoPlaying: false,
      highlightedStageId: null,
      poseRequest: s.poseRequest + 1,
    })),
}));

/** Sum of the per-link rates, for the panel header. */
export function totalPacketsPerMinute(links: Record<string, LinkStats>): number {
  let sum = 0;
  for (const l of Object.values(links)) sum += l.packetsPerMinute;
  return sum;
}
